import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { resizeImage, getTaskStatus } from '../../services/chatApi';
import { Download, Upload, Scaling, RefreshCw, Loader2, LayoutTemplate, Link as LinkIcon, Unlink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};



const ResizeImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();
    
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Dimensions State
    const [originalDims, setOriginalDims] = useState({ w: 0, h: 0 });
    const [targetWidth, setTargetWidth] = useState('');
    const [targetHeight, setTargetHeight] = useState('');
    const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
    const [method, setMethod] = useState('Bilinear');

    const pollInterval = useRef(null);
    const imgRef = useRef(null);

    // Handle incoming navigation from other tools
    const resetParams = useCallback(() => {
        setTargetWidth('');
        setTargetHeight('');
        setOriginalDims({ w: 0, h: 0 });
        setMaintainAspectRatio(true);
        setError(null);
    }, []);

    // 2. Hook
    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);

    useEffect(() => {
        return () => clearInterval(pollInterval.current);
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            resetParams();
        }
    };

    // Load original dimensions when image loads
    const onImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        setOriginalDims({ w: naturalWidth, h: naturalHeight });
        setTargetWidth(naturalWidth);
        setTargetHeight(naturalHeight);
    };

    // Handle Dimension Changes with Aspect Ratio logic
    const handleWidthChange = (e) => {
        const val = parseInt(e.target.value) || '';
        setTargetWidth(val);
        if (maintainAspectRatio && originalDims.w > 0 && val) {
            const ratio = originalDims.h / originalDims.w;
            setTargetHeight(Math.round(val * ratio));
        }
    };

    const handleHeightChange = (e) => {
        const val = parseInt(e.target.value) || '';
        setTargetHeight(val);
        if (maintainAspectRatio && originalDims.h > 0 && val) {
            const ratio = originalDims.w / originalDims.h;
            setTargetWidth(Math.round(val * ratio));
        }
    };

    const handleProcess = async () => {
        if (!file || !targetWidth || !targetHeight) return;
        
        setIsLoading(true);
        setError(null);
        setProcessedImage(null);
        
        try {
            const params = {
                width: targetWidth,
                height: targetHeight,
                method: method
            };

            const startResponse = await resizeImage(file, params);
            const taskId = startResponse.task_id;

            if (!taskId) throw new Error("No Task ID returned");

            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state;

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        const result = statusRes.result || statusRes.info;
                        if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }
                        if (result.download_url) {
                            setProcessedImage(result.download_url);
                        } else {
                            setError("Invalid response format.");
                        }
                        setIsLoading(false);

                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('resizeImage.errors.generic') || "Resize failed");
                    }
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error.");
                }
            }, 2000); 

        } catch (err) {
            console.error(err);
            setError(t('resizeImage.errors.generic') || "Request failed");
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        try {
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `resized_${targetWidth}x${targetHeight}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            window.open(processedImage, '_blank');
        }
    };

    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        resetParams();
        clearInterval(pollInterval.current);
    };
    
    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : (
                <div className="max-w-6xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange mb-2">
                        {t('resizeImage.title') || "Resize Image"}
                    </h3>
                    <p className="text-slate-600 mb-8 max-w-2xl">
                        {t('resizeImage.description') || "Change image dimensions while maintaining quality."}
                    </p>

                    {!file ? (
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                            <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="resize-upload" />
                            <label htmlFor="resize-upload" className="cursor-pointer flex flex-col items-center">
                                <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                    <Upload size={32} className="text-brand-orange" />
                                </div>
                                <span className="text-xl font-medium text-slate-700 mb-2">{t('resizeImage.upload.label') || "Upload Image"}</span>
                            </label>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            
                            {/* Left Column: Preview */}
                            <div className="lg:col-span-8">
                                <div className="bg-slate-100 p-4 rounded-lg border border-slate-200 flex justify-center items-center min-h-[400px]">
                                    {/* Helper to get natural dimensions */}
                                    <img 
                                        src={previewUrl} 
                                        onLoad={onImageLoad} 
                                        className="hidden" 
                                        alt="hidden helper" 
                                    />
                                    
                                    {processedImage ? (
                                        <div className="relative">
                                             <div className="absolute inset-0 bg-[url('https://img.freepik.com/free-vector/gray-checker-pattern-background_1017-31997.jpg')] bg-cover opacity-20 z-0"></div>
                                            <img src={processedImage} alt="Result" className="relative z-10 max-h-[500px] w-auto shadow-lg" />
                                        </div>
                                    ) : (
                                        <img src={previewUrl} alt="Preview" className="max-h-[500px] w-auto shadow-sm" />
                                    )}
                                </div>
                                
                                {processedImage && (
                                    <div className="mt-4">
                                        <ToolNavigator currentImage={processedImage} />
                                    </div>
                                )}
                                
                                {error && <div className="mt-4 text-red-600 bg-red-50 p-3 rounded">{error}</div>}
                            </div>

                            {/* Right Column: Controls */}
                            <div className="lg:col-span-4 space-y-6">
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                                    <h4 className="font-semibold mb-4 flex items-center gap-2 text-brand-orange">
                                        <LayoutTemplate size={20} /> {t('resizeImage.controls.title') || "Settings"}
                                    </h4>

                                    {!processedImage ? (
                                        <>
                                            {/* Dimensions Inputs */}
                                            <div className="space-y-4 mb-6">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <button 
                                                        onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
                                                        className={`p-1 rounded ${maintainAspectRatio ? 'bg-orange-100 text-brand-orange' : 'text-slate-400'}`}
                                                        title="Maintain Aspect Ratio"
                                                    >
                                                        {maintainAspectRatio ? <LinkIcon size={16} /> : <Unlink size={16} />}
                                                    </button>
                                                    <span className="text-xs text-slate-500">
                                                        {maintainAspectRatio ? "Aspect Ratio Locked" : "Aspect Ratio Unlocked"}
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-500 mb-1">WIDTH (PX)</label>
                                                        <input 
                                                            type="number" 
                                                            value={targetWidth} 
                                                            onChange={handleWidthChange}
                                                            className="w-full p-2 border border-slate-300 rounded focus:border-brand-orange focus:ring-1 focus:ring-brand-orange" 
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-500 mb-1">HEIGHT (PX)</label>
                                                        <input 
                                                            type="number" 
                                                            value={targetHeight} 
                                                            onChange={handleHeightChange}
                                                            className="w-full p-2 border border-slate-300 rounded focus:border-brand-orange focus:ring-1 focus:ring-brand-orange" 
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-500 mb-1">ALGORITHM</label>
                                                    <select 
                                                        value={method} 
                                                        onChange={(e) => setMethod(e.target.value)}
                                                        className="w-full p-2 border border-slate-300 rounded"
                                                    >
                                                        <option value="Bilinear">Bilinear (Standard)</option>
                                                        <option value="Bicubic">Bicubic (Smoother)</option>
                                                        <option value="Lanczos">Lanczos (High Quality)</option>
                                                        <option value="Nearest">Nearest Neighbor (Pixel Art)</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="p-3 bg-slate-50 rounded text-xs text-slate-500 border border-slate-100 mb-4">
                                                Original: {originalDims.w} x {originalDims.h} px
                                            </div>

                                            <button 
                                                onClick={handleProcess} 
                                                disabled={isLoading || !targetWidth || !targetHeight} 
                                                className="w-full bg-brand-orange text-white py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center gap-2 font-semibold disabled:opacity-50"
                                            >
                                                {isLoading ? <Loader2 className="animate-spin" /> : <Scaling size={20} />} 
                                                {t('resizeImage.buttons.process') || "Resize Image"}
                                            </button>
                                        </>
                                    ) : (
                                        <div className="space-y-3">
                                            <button onClick={handleDownload} className="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 transition flex items-center justify-center gap-2 font-semibold">
                                                <Download size={18} /> {t('resizeImage.buttons.download') || "Download"}
                                            </button>
                                            <button onClick={() => setProcessedImage(null)} className="w-full bg-slate-200 text-slate-700 py-3 rounded-md hover:bg-slate-300 transition flex items-center justify-center gap-2">
                                                <LayoutTemplate size={18} /> {t('resizeImage.buttons.edit') || "Edit Again"}
                                            </button>
                                            <button onClick={resetTool} className="w-full bg-slate-100 text-slate-700 py-3 rounded-md hover:bg-slate-200 transition flex items-center justify-center gap-2">
                                                <RefreshCw size={18} /> {t('resizeImage.buttons.newImage') || "New Image"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ResizeImage;
